import React, { useState } from 'react';
import axios from 'axios';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { toast } from 'sonner';
import { KeyRound, Loader2, MessageCircle, ShieldCheck } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const PURPOSE = 'alterar_senha';

export const CustomerPasswordChange = ({ kakoId, cardClass = '', inputClass = '' }) => {
  const [step, setStep] = useState(0);   // 0 fechado · 1 código · 2 nova senha
  const [challengeId, setChallengeId] = useState('');
  const [code, setCode] = useState('');
  const [token, setToken] = useState('');
  const [pass, setPass] = useState('');
  const [pass2, setPass2] = useState('');
  const [busy, setBusy] = useState(false);

  const token_ = () => localStorage.getItem('customer_token');

  const requestCode = async () => {
    setBusy(true);
    try {
      const r = await axios.post(`${BACKEND_URL}/api/kako-auth/request-code`,
        { kako_id: kakoId, purpose: PURPOSE });
      setChallengeId(r.data.challenge_id);
      setStep(1);
      toast.success('Código enviado no chat do app KAKO.');
    } catch (e) {
      toast.error(e.response?.data?.detail || e.response?.data?.error
        || 'Não foi possível enviar o código agora.');
    }
    setBusy(false);
  };

  const verifyCode = async () => {
    setBusy(true);
    try {
      const r = await axios.post(`${BACKEND_URL}/api/kako-auth/verify-code`,
        { kako_id: kakoId, purpose: PURPOSE, challenge_id: challengeId, code: code.trim() });
      setToken(r.data.verification_token);
      setStep(2);
      toast.success('Código confirmado. Agora defina a nova senha.');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Código inválido ou expirado.');
    }
    setBusy(false);
  };

  const savePassword = async () => {
    if (pass.length < 8) { toast.error('A senha precisa ter pelo menos 8 caracteres.'); return; }
    if (pass !== pass2) { toast.error('As duas senhas não são iguais.'); return; }
    setBusy(true);
    try {
      await axios.post(`${BACKEND_URL}/api/customer-auth/me/change-password`,
        { verification_token: token, new_password: pass },
        { headers: { Authorization: `Bearer ${token_()}` } });
      toast.success('Senha alterada com sucesso.');
      setStep(0); setCode(''); setPass(''); setPass2(''); setToken('');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Não foi possível alterar a senha.');
    }
    setBusy(false);
  };

  return (
    <Card className={cardClass} data-testid="customer-password-card">
      <CardHeader className="pb-3">
        <CardTitle className="font-display flex items-center gap-2 text-lg tracking-tight text-slate-900">
          <span className="flex h-8 w-8 items-center justify-center rounded-full bg-gradient-to-br from-orange-100 to-purple-100 text-orange-600">
            <KeyRound className="h-4 w-4" />
          </span>
          Alterar senha
        </CardTitle>
      </CardHeader>
      <CardContent className="pt-0">
        {step === 0 && (
          <div className="space-y-2.5">
            <p className="text-sm text-slate-600">
              Para trocar sua senha, enviamos um código no <b>chat do app KAKO</b>, na sua conta.
            </p>
            <Button size="sm" disabled={busy} onClick={requestCode}
              data-testid="customer-password-request-code"
              className="rounded-full bg-gradient-to-r from-orange-500 to-purple-600 text-white">
              {busy ? <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" />
                : <MessageCircle className="mr-1.5 h-3.5 w-3.5" />}
              Receber código no chat
            </Button>
          </div>
        )}

        {step === 1 && (
          <div className="space-y-2.5" data-testid="customer-password-code-step">
            <p className="text-sm text-slate-600">
              Digite o código que chegou no chat do app KAKO.
            </p>
            <Input value={code} inputMode="numeric" maxLength={6} placeholder="Código de 6 dígitos"
              data-testid="customer-password-code-input" className={inputClass}
              onChange={(e) => setCode(e.target.value.replace(/\D/g, ''))} />
            <div className="flex flex-wrap gap-2">
              <Button size="sm" disabled={busy || code.length < 6} onClick={verifyCode}
                data-testid="customer-password-verify-code"
                className="rounded-full bg-gradient-to-r from-orange-500 to-purple-600 text-white">
                <ShieldCheck className="mr-1.5 h-3.5 w-3.5" /> Confirmar código
              </Button>
              <Button size="sm" variant="outline" disabled={busy} onClick={requestCode}
                data-testid="customer-password-resend" className="rounded-full">
                Reenviar
              </Button>
              <Button size="sm" variant="ghost" onClick={() => setStep(0)}
                data-testid="customer-password-cancel-code">Cancelar</Button>
            </div>
          </div>
        )}

        {step === 2 && (
          <div className="space-y-2.5" data-testid="customer-password-new-step">
            <Input type="password" value={pass} placeholder="Nova senha (mín. 8 caracteres)"
              data-testid="customer-password-new" className={inputClass}
              onChange={(e) => setPass(e.target.value)} />
            <Input type="password" value={pass2} placeholder="Repita a nova senha"
              data-testid="customer-password-new2" className={inputClass}
              onChange={(e) => setPass2(e.target.value)} />
            <div className="flex flex-wrap gap-2">
              <Button size="sm" disabled={busy} onClick={savePassword}
                data-testid="customer-password-save"
                className="rounded-full bg-gradient-to-r from-orange-500 to-purple-600 text-white">
                {busy && <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" />} Salvar nova senha
              </Button>
              <Button size="sm" variant="ghost" onClick={() => setStep(0)}
                data-testid="customer-password-cancel-new">Cancelar</Button>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default CustomerPasswordChange;
